import { Alert, Button, Card, Group, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { z } from "zod";
import { semanticColor } from "@how/design-system";
import { ErrorState, LoadingState, PageHeader } from "@how/ui";
import { CommunicationsTabs } from "./CommunicationsTabs";
import { useSaveTextUs, useTextUsSettings, type TextUsSettings } from "./text-us-queries";

/** The same rules as set_website_text_us, so a refusal is seen while typing. */
export const textUsSchema = z.object({
  enabled: z.boolean(),
  buttonLabel: z
    .string()
    .trim()
    .min(1, "The button needs a label")
    .max(24, "Keep the label to 24 characters")
    .regex(/^[^\n\r\t]*$/, "One line only"),
  greeting: z
    .string()
    .trim()
    .min(1, "Write a greeting")
    .max(80, "Keep the greeting to 80 characters")
    .regex(/^[^\n\r\t]*$/, "One line only"),
});
type TextUsForm = z.infer<typeof textUsSchema>;

/**
 * Communications → Text us (ADR 0074): the floating "Text us" button on the church's website. Who answers the
 * texting inbox (communications.inbox) turns it on and words it. A church that cannot text is told why it can't be
 * turned on, and the switch stays off.
 */
export function TextUsPage() {
  const settings = useTextUsSettings();
  return (
    <>
      <PageHeader
        title="Communications"
        description="Who the church can reach, what it says, and what it has sent."
      />
      <CommunicationsTabs active="text-us" />
      {settings.isPending ? (
        <LoadingState rows={4} />
      ) : settings.isError ? (
        <ErrorState
          message="Could not load the Text us settings."
          onRetry={() => {
            void settings.refetch();
          }}
        />
      ) : (
        <TextUsCard settings={settings.data} />
      )}
    </>
  );
}

function TextUsCard({ settings }: { readonly settings: TextUsSettings }) {
  const save = useSaveTextUs();
  const heading = useRef<HTMLHeadingElement>(null);
  const form = useForm<TextUsForm>({
    mode: "controlled",
    initialValues: {
      enabled: settings.enabled,
      buttonLabel: settings.buttonLabel,
      greeting: settings.greeting,
    },
    validate: zod4Resolver(textUsSchema),
  });
  // A save answers with the setting as stored; the form starts again from it, so Save reads as done.
  useEffect(() => {
    form.setInitialValues({
      enabled: settings.enabled,
      buttonLabel: settings.buttonLabel,
      greeting: settings.greeting,
    });
    form.resetDirty();
  }, [settings.enabled, settings.buttonLabel, settings.greeting]);

  const submit = form.onSubmit((values) => {
    save.mutate(
      {
        enabled: values.enabled,
        buttonLabel: values.buttonLabel.trim(),
        greeting: values.greeting.trim(),
      },
      {
        onSuccess: (saved) => {
          // Save turns disabled once saved; focus goes to the card's heading, never to the page.
          heading.current?.focus();
          notifications.show({
            color: "teal",
            title: "Text us saved",
            message: saved.live
              ? "The Text us button is on your website now."
              : "The Text us button is off on your website.",
          });
        },
        onError: (error) => {
          notifications.show({ color: "red", title: "Not saved", message: error.message });
        },
      },
    );
  });

  const confirmation = `Thanks <first name>, we got your message and will text you back here soon. Msg frequency varies. Msg & data rates may apply. Reply HELP for help.\n${settings.senderName}: Reply STOP to opt out`;

  return (
    <Card component="section" aria-labelledby="text-us-title">
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <Title order={2} size="h4" id="text-us-title" ref={heading} tabIndex={-1}>
            Text us on your website
          </Title>
          <Text size="sm" c="dimmed">
            A &ldquo;Text us&rdquo; button at the bottom right of every page of your website.
            Visitors leave their name, mobile number and message; it arrives in Communications
            &rarr; <Link to="/communications/inbox">Inbox</Link>, and you answer it there as any
            text. Your church has one texting inbox, so every conversation goes there.
          </Text>

          {!settings.available ? (
            <Alert color={semanticColor("warning")} variant="light" title="Texting isn't set up">
              Your church can&apos;t send texts yet, so the Text us button can&apos;t be turned on.
              DCX Logic sets up texting for your church: email support@dcxlogic.com.
            </Alert>
          ) : null}
          {settings.available && !settings.website ? (
            <Alert color={semanticColor("warning")} variant="light" title="No website">
              Your plan has no HoW website, so there is nowhere to show the button.
            </Alert>
          ) : null}
          {settings.pilot ? (
            <Alert color={semanticColor("info")} variant="light" title="Pilot texting is on">
              Only pilot testers get texts from your church, so a visitor who isn&apos;t one gets no
              confirmation text. Their message still reaches your inbox, and the conversation says
              why no text went.
            </Alert>
          ) : null}

          <Switch
            label="Show the Text us button on your website"
            disabled={!settings.available && !form.values.enabled}
            {...form.getInputProps("enabled", { type: "checkbox" })}
          />
          <TextInput
            label="Button label"
            description="What the button says, such as Text us or Message us."
            maxLength={24}
            {...form.getInputProps("buttonLabel")}
          />
          <TextInput
            label="Greeting"
            description="The line at the top of the panel the button opens."
            maxLength={80}
            {...form.getInputProps("greeting")}
          />

          <Stack gap={4}>
            <Text size="sm" fw={600}>
              What happens when a visitor sends a message
            </Text>
            <Text size="sm">
              Their message opens a conversation in your Inbox, and they are matched to the person
              with that mobile number, or added as someone new. If they ticked the box to be texted,
              their yes to texts is recorded, and they get this text right away:
            </Text>
            <Text
              size="sm"
              ff="monospace"
              p="xs"
              bg="var(--how-surface-sunken)"
              style={{ whiteSpace: "pre-wrap", borderRadius: 8 }}
              data-testid="text-us-confirmation"
            >
              {confirmation}
            </Text>
            <Text size="sm" c="dimmed">
              If they didn&apos;t tick it, no text is sent; follow up another way.
            </Text>
          </Stack>

          <Group justify="flex-end">
            <Button type="submit" loading={save.isPending} disabled={!form.isDirty()}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}
